import { VideoPokerVariant, getVariantConfig, setCurrentVariant, getCurrentVariant } from '../config/videopoker.config';

export class VideoPokerVariantSelector {
    private container: HTMLDivElement;
    private isOpen: boolean = false;
    
    constructor() {
        this.container = document.createElement('div');
        this.container.id = 'variant-selector';
        this.setupStyles();
        this.createUI();
        document.body.appendChild(this.container);
    }
    
    private setupStyles(): void {
        const style = document.createElement('style');
        style.textContent = `
            #variant-selector {
                position: fixed;
                top: 10px;
                right: 10px;
                z-index: 1000;
                font-family: 'Orbitron', Arial, sans-serif;
            }
            
            .variant-button {
                background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
                color: black;
                border: 2px solid #FFFF00;
                padding: 10px 20px;
                border-radius: 25px;
                cursor: pointer;
                font-size: 14px;
                font-weight: bold;
                text-transform: uppercase;
                transition: all 0.3s ease;
                box-shadow: 0 4px 15px rgba(255, 255, 0, 0.3);
            }
            
            .variant-button:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(255, 255, 0, 0.5);
                background: linear-gradient(135deg, #FFA500 0%, #FFD700 100%);
            }
            
            .variant-menu {
                display: none;
                position: absolute;
                top: 50px;
                right: 0;
                background: rgba(0, 0, 0, 0.95);
                border: 2px solid #FFFF00;
                border-radius: 10px;
                padding: 10px;
                min-width: 280px;
                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.8);
            }
            
            .variant-menu.open {
                display: block;
                animation: slideDown 0.3s ease;
            }
            
            @keyframes slideDown {
                from {
                    opacity: 0;
                    transform: translateY(-10px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            
            .variant-item {
                padding: 12px 15px;
                margin: 5px 0;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid transparent;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.3s ease;
            }
            
            .variant-item:hover {
                background: rgba(255, 255, 255, 0.2);
                border-color: #FFFF00;
                transform: translateX(5px);
            }
            
            .variant-item.active {
                background: rgba(255, 255, 0, 0.2);
                border-color: #FFFF00;
            }
            
            .variant-name {
                color: #FFFF00;
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 4px;
            }
            
            .variant-description {
                color: #FFFFFF;
                font-size: 11px;
                opacity: 0.9;
                margin-bottom: 2px;
            }
            
            .variant-paytable {
                color: #FFD700;
                font-size: 10px;
                opacity: 0.8;
            }
            
            .variant-preview {
                display: inline-block;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                margin-right: 8px;
                vertical-align: middle;
                background: linear-gradient(135deg, #FFD700, #FFA500);
            }
        `;
        document.head.appendChild(style);
    }
    
    private createUI(): void {
        const currentVariant = getCurrentVariant();
        
        // Create main button
        const button = document.createElement('button');
        button.className = 'variant-button';
        button.innerHTML = `🎰 ${currentVariant.name}`;
        button.onclick = () => this.toggleMenu();
        
        // Create dropdown menu
        const menu = document.createElement('div');
        menu.className = 'variant-menu';
        
        // Add variant options
        Object.values(VideoPokerVariant).forEach(variantType => {
            const config = getVariantConfig(variantType);
            const item = document.createElement('div');
            item.className = 'variant-item';
            if (config.id === currentVariant.id) {
                item.classList.add('active');
            }
            
            // Create variant preview dot
            const preview = document.createElement('span');
            preview.className = 'variant-preview';
            
            const nameDiv = document.createElement('div');
            nameDiv.className = 'variant-name';
            nameDiv.appendChild(preview);
            nameDiv.appendChild(document.createTextNode(config.name));
            
            const descDiv = document.createElement('div');
            descDiv.className = 'variant-description';
            descDiv.textContent = config.description;
            
            const paytableDiv = document.createElement('div');
            paytableDiv.className = 'variant-paytable';
            paytableDiv.textContent = config.paytableInfo;
            
            item.appendChild(nameDiv);
            item.appendChild(descDiv);
            item.appendChild(paytableDiv);
            
            item.onclick = () => this.selectVariant(variantType);
            menu.appendChild(item);
        });
        
        this.container.appendChild(button);
        this.container.appendChild(menu);
    }
    
    private toggleMenu(): void {
        this.isOpen = !this.isOpen;
        const menu = this.container.querySelector('.variant-menu');
        if (menu) {
            if (this.isOpen) {
                menu.classList.add('open');
            } else {
                menu.classList.remove('open');
            }
        }
    }
    
    private selectVariant(variantType: VideoPokerVariant): void {
        setCurrentVariant(variantType);
        
        // Close the menu
        this.isOpen = false;
        const menu = this.container.querySelector('.variant-menu');
        if (menu) {
            menu.classList.remove('open');
        }
        
        // Notify the scene to update itself
        const event = new CustomEvent('variantChanged', { 
            detail: { variant: variantType } 
        });
        window.dispatchEvent(event);
    }
    
    public destroy(): void {
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}